import { Link } from '@tanstack/react-router';
import { Building2, LogOut, Menu as IconoMenu, Moon, Search, Sun, SunMoon } from 'lucide-react';
import { useState } from 'react';
import { useSesion } from '@/api/sesion';
import { Menu, MenuContenido, MenuDisparador, MenuEtiqueta, MenuItem, MenuSeparador } from '@/components/ui/overlay';
import { useTema } from '@/lib/tema';
import { cn, iniciales } from '@/lib/utils';
import { MODULOS, tieneAlguno } from './navegacion';
import { Notificaciones } from './Notificaciones';

interface Props {
  onAbrirBuscador: () => void;
  sucursales: { id: string; nombre: string }[];
}

const ETIQUETA_ROL = { DUENO: 'Dueño', RECEPCION: 'Recepción', BARBERO: 'Barbero' } as const;

export function TopBar({ onAbrirBuscador, sucursales }: Props) {
  const { usuario, sucursalId, cambiarSucursal, cerrarSesion } = useSesion();
  const { tema, cambiarTema } = useTema();
  const [menuMovil, setMenuMovil] = useState(false);
  const modulos = MODULOS.filter((m) => tieneAlguno(usuario?.permisos ?? [], m.permisos));
  const sucursal = sucursales.find((s) => s.id === sucursalId);

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-surface/92 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
      <div className="mx-auto flex h-14 max-w-[1500px] items-center gap-3 px-4 sm:px-6">
        <button
          type="button"
          className="rounded-md p-2 text-foreground-muted hover:bg-muted xl:hidden"
          onClick={() => setMenuMovil((v) => !v)}
          aria-label="Abrir menú de módulos"
          aria-expanded={menuMovil}
        >
          <IconoMenu className="size-5" />
        </button>

        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="Barbones, ir al inicio">
          <img src="/logo-sello.jpeg" alt="" className="size-8 rounded-full ring-1 ring-border dark:invert" />
          <span className="hidden font-display text-[19px] font-semibold tracking-[0.12em] sm:block">BARBONES</span>
        </Link>

        <nav className="ml-2 hidden items-center gap-0.5 xl:flex 2xl:ml-4" aria-label="Módulos">
          {modulos.map((m) => (
            <Link
              key={m.id}
              to={m.to}
              activeOptions={{ exact: m.to === '/' }}
              className="relative whitespace-nowrap rounded-md px-2.5 py-1.5 text-[13.5px] 2xl:px-3 text-foreground-muted transition-colors hover:text-foreground"
              activeProps={{ className: 'text-foreground font-medium' }}
            >
              {({ isActive }) => (
                <>
                  {m.etiqueta}
                  <span
                    className={cn(
                      'absolute inset-x-2.5 -bottom-[13px] 2xl:inset-x-3 h-0.5 rounded-full bg-accent transition-opacity',
                      isActive ? 'opacity-100' : 'opacity-0',
                    )}
                    aria-hidden
                  />
                </>
              )}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={onAbrirBuscador}
            className="flex h-9 items-center gap-2 rounded-md border border-border bg-background/60 px-3 text-[13px] text-foreground-muted transition-colors hover:border-accent/60 sm:w-64 xl:w-40 2xl:w-64"
            aria-label="Buscar (Ctrl+K)"
          >
            <Search className="size-4" />
            <span className="hidden sm:inline">Buscar…</span>
            <kbd className="ml-auto hidden rounded border border-border px-1.5 text-[11px] sm:inline">Ctrl K</kbd>
          </button>

          <Notificaciones />

          <Menu>
            <MenuDisparador asChild>
              <button
                type="button"
                className="flex items-center gap-2 rounded-md py-1 pl-1 pr-2 hover:bg-muted"
                aria-label="Menú de usuario"
              >
                <span className="grid size-8 place-items-center rounded-full bg-primary font-display text-[13px] text-on-primary">
                  {iniciales(usuario?.nombre ?? '?')}
                </span>
                <span className="hidden max-w-44 text-left leading-tight 2xl:block">
                  <span className="block truncate text-[13px] font-medium">{usuario?.nombre}</span>
                  <span className="block truncate text-[11.5px] text-foreground-muted" title={sucursal?.nombre}>
                    {usuario ? ETIQUETA_ROL[usuario.rol] : ''} · {sucursal?.nombre ?? 'Sucursal'}
                  </span>
                </span>
              </button>
            </MenuDisparador>
            <MenuContenido align="end" className="w-60">
              <MenuEtiqueta>Sucursal</MenuEtiqueta>
              {sucursales.map((s) => (
                <MenuItem key={s.id} onSelect={() => cambiarSucursal(s.id)}>
                  <Building2 />
                  <span className="flex-1">{s.nombre}</span>
                  {s.id === sucursalId && <span className="size-1.5 rounded-full bg-accent" aria-label="actual" />}
                </MenuItem>
              ))}
              <MenuSeparador />
              <MenuEtiqueta>Apariencia</MenuEtiqueta>
              {(
                [
                  ['sistema', 'Según el sistema', SunMoon],
                  ['claro', 'Claro', Sun],
                  ['oscuro', 'Oscuro', Moon],
                ] as const
              ).map(([valor, etiqueta, Icono]) => (
                <MenuItem key={valor} onSelect={() => cambiarTema(valor)}>
                  <Icono />
                  <span className="flex-1">{etiqueta}</span>
                  {tema === valor && <span className="size-1.5 rounded-full bg-accent" aria-label="actual" />}
                </MenuItem>
              ))}
              <MenuSeparador />
              <MenuItem onSelect={() => void cerrarSesion()} className="text-destructive">
                <LogOut />
                Cerrar sesión
              </MenuItem>
            </MenuContenido>
          </Menu>
        </div>
      </div>

      {menuMovil && (
        <nav className="border-t border-border bg-surface px-4 py-2 xl:hidden" aria-label="Módulos">
          <div className="grid grid-cols-2 gap-1 sm:grid-cols-4">
            {modulos.map((m) => (
              <Link
                key={m.id}
                to={m.to}
                activeOptions={{ exact: m.to === '/' }}
                onClick={() => setMenuMovil(false)}
                className="flex items-center gap-2 rounded-md px-3 py-2.5 text-[14px] text-foreground-muted hover:bg-muted"
                activeProps={{ className: 'bg-muted text-foreground font-medium' }}
              >
                <m.icono className="size-4" aria-hidden />
                {m.etiqueta}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}
